import { action, computed, observable, makeAutoObservable } from "mobx";
import CommonUtil from "utils/CommonUtil";
import ObjectUtil from "utils/ObjectUtil";
import StringUtil from "utils/StringUtil";

import MenuInfo from "../model/MenuInfo";
import menuRepository from "../repository/MenuRepository";

class MenuStore {
  @observable menuList?: MenuInfo[] = [];

  @observable selectedTopMenu: MenuInfo | undefined;
  @observable currentTopMenu: MenuInfo | undefined;
  @observable currentMenu: MenuInfo | undefined;
  @observable mainSubMenuShow: boolean = false;

  @observable menuTree?: MenuInfo[] = [];

  private _menuKnd: string = "";
  private _menuMap = new Map<string, MenuInfo>();

  constructor() {
    makeAutoObservable(this);
  }

  public get menuKnd() {
    return this._menuKnd;
  }


  @action
  public setMenuList(menuList?: MenuInfo[]) {
    this.menuList = menuList;
  }

  @action
  public setMenuTree(menuList?: MenuInfo[]) {
    this.menuTree = menuList;
  }

  @computed
  public get currentMenuInfoList(): MenuInfo[] {
    return this.menuList?.filter(menu => menu.active) || [];
  }

  @action
  public setCurrentMenu(menu: MenuInfo) {
    this.menuList?.forEach((menu: MenuInfo) => {
      menu.setActive(false);
      menu.setOpened(false);
    });

    menu.setActive(true);

    let upperMenu = this.getUpperMenu(menu)
    while(upperMenu) {
      this.currentTopMenu = upperMenu;

      upperMenu.setActive(true);
      upperMenu.setOpened(true);

      upperMenu = this.getUpperMenu(upperMenu);
    }
    if(!this.currentTopMenu) {
      this.currentTopMenu = menu;
    }
  }

  @action
  public removeCurrentMenu() {
    this.menuList?.forEach((menu: MenuInfo) => {
      menu.setActive(false);
    });

    this.selectedTopMenu = undefined;
    this.currentTopMenu = undefined;
    this.currentMenu = undefined;
  }

  public getUpperMenu(menu: MenuInfo) {
    return this._menuMap.get(menu.getUpperMenuId());
  }

  public getTopMenu(menu: MenuInfo) {
    const upperMenu = this.getUpperMenu(menu);

    if (upperMenu) {
      return this.getTopMenu(upperMenu);
    }

    return menu;
  }

  public get firstPathMenu() {
    return this.getFirstPathMenu();
  }
  @action
  public setMainSubMenuShow(mainSubMenuShow: boolean) {
    this.mainSubMenuShow = mainSubMenuShow;
  }

  /**
   * 경로가 설정된 첫번째 메뉴 반환
   *
   * @param upperMenu
   */
  public getFirstPathMenu(upperMenu?: MenuInfo) {
    if (this.menuList == null || this.menuList.length === 0) {
      return;
    }

    if (upperMenu == null) {
      return this.getFirstPathMenu(this.menuList[0]);
    }

    const childMenuList: MenuInfo[] = [];

    const addChildMenu = (upperMenu: MenuInfo) => {
      this.menuList?.forEach((menuInfo: MenuInfo) => {
        if (menuInfo.getUpperMenuId() === upperMenu.getMenuId()) {
          childMenuList.push(menuInfo);

          addChildMenu(menuInfo);
        }
      });
    };

    addChildMenu(upperMenu);

    return childMenuList.find((menuInfo) => StringUtil.isNotEmpty(menuInfo.getMenuPath()));
  }

  @action
  public setSelectedTopMenu(selectedTopMenu: MenuInfo) {
    if (this.selectedTopMenu === selectedTopMenu) {
      this.removeSelectedTopMenu();
    } else {
      this.selectedTopMenu = selectedTopMenu;
    }
  }

  @action
  public removeSelectedTopMenu() {
    this.selectedTopMenu = undefined;
  }

  @action
  public toggleOpened(menu: MenuInfo) {
    menu.setOpened(!menu.isOpened());
  }

  /**
   * 전체 목록 조회
   *
   * @param menuKnd
   */
  public async readAllList(menuKnd: string) {
    this._menuKnd = menuKnd;

    let result = await menuRepository.readAllList(menuKnd);
    const treeList = CommonUtil.toSortedTreeList(result, "menuId", "upperMenuId");
    const menuList = treeList.map((menu) => new MenuInfo(menu));

    menuList.forEach((menu) => this._menuMap.set(menu.getMenuId(), menu));

    this.setMenuList(menuList);
    this.setMenuTree(this.buildTree(menuList));
    console.log("readAllList", "tree" , this.menuTree);
  }

  public buildTree(items: MenuInfo[], parentId:string|null = null) {
    return items
        .filter(item => item.upperMenuId === parentId)
        .map(item => item.setChildren(this.buildTree(items, item.menuId)));
  }

  /**
   * 전체 목록 갱신
   */
  public async reloadAllList() {
    if (StringUtil.isNotEmpty(this._menuKnd)) {
      this.readAllList(this._menuKnd);
    }
  }

  /**
   * 화면 경로에 해당하는 메뉴 재설정
   *
   * @param pathname
   * @param reptCnt
   */
  public resetLocation(pathname: string) {
    if (this.menuList) {
      let matchedMenu: MenuInfo | undefined;

      for (let i = this.menuList.length - 1; i >= 0; i--) {
        const menu = this.menuList[i];

        if (menu.getMenuPath() &&  menu.getMenuPath().startsWith(pathname)) {
          matchedMenu = menu;
          break;
        }
      }
      if(matchedMenu !== undefined) {
        this.removeCurrentMenu();

        this.menuList.forEach((menu: MenuInfo) => {
          menu.setActive(false);
        });

        this.setCurrentMenu(matchedMenu);
        matchedMenu.setActive(true);
      }
    }
  }

  /**
   * 특정 경로의 상위메뉴에 해당하는 하위메뉴 목록 반환
   *
   * @param upperMenuId
   */
  @computed
  public getSubMenuList(upperMenuId?: string|null) {
    return this.menuList?.filter((menu) => menu.upperMenuId === upperMenuId);
  }
}
const menuStore = new MenuStore();
export default menuStore;
